export default function ViewOrderModal({ open, onClose, order }) {
  if (!open || !order) return null;

  const isGiftWrapItem = (p) => (p?.sku || "").toUpperCase() === "GIFT-WRAP";

  const giftWrapLine = (order.products || []).find(isGiftWrapItem);
  const normalLines = (order.products || []).filter((p) => !isGiftWrapItem(p));

  const subtotal = normalLines.reduce(
    (sum, p) => sum + Number(p?.price || 0) * Number(p?.quantity || 0),
    0
  );

  const giftWrapTotal = giftWrapLine
    ? Number(giftWrapLine?.price || 0) * Number(giftWrapLine?.quantity || 1)
    : 0;

  const deliveryCharge = Number(order.delivery_charge || 0);
  const orderTotal = Number(order.order_total || 0);
  const amountPaid = Number(order.amount_paid || 0);
  const amountToCollect = Number(
    order.amount_to_collect ?? Math.max(orderTotal - amountPaid, 0)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-xl max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Order Details — {order.merchant_order_id || order._id || order.id}
            </h2>
            <p className="text-xs text-slate-500">
              Consignment: {order.consignment_id || "—"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* Body (scroll) */}
        <div className="max-h-[calc(90vh-64px-56px)] overflow-y-auto px-5 py-5 space-y-5">
          {/* Status row */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <StatusRow
                label="Order Status"
                type="order"
                value={order.order_status}
              />
              <StatusRow
                label="Payment Status"
                type="payment"
                value={order.payment_status}
              />
            </div>
          </div>

          {/* Recipient */}
          <Section title="Recipient Info">
            <Detail label="Name" value={order.recipient_name} />
            <Detail label="Phone" value={order.recipient_phone} />
            <Detail label="Address" value={order.recipient_address} />
            {order.email ? <Detail label="Email" value={order.email} /> : null}
          </Section>

          {/* Order Info */}
          <Section title="Order Information">
            <Detail
              label="Delivery Type"
              value={
                Number(order.delivery_type) === 48 ? "Regular" : "On Demand"
              }
            />
            <Detail
              label="Item Type"
              value={Number(order.item_type) === 1 ? "Document" : "Parcel"}
            />
            <Detail label="Item Quantity" value={order.item_quantity} />
            <Detail label="Weight" value={`${order.item_weight} KG`} />

            {order.item_description ? (
              <Detail label="Description" value={order.item_description} />
            ) : null}

            {order.note ? <Detail label="Note" value={order.note} /> : null}

            {/* gift_wrap boolean + product line */}
            <div className="flex justify-between text-sm">
              <span className="font-medium text-slate-600">Gift Wrap</span>
              <span className="text-slate-900 text-right max-w-[60%] break-words">
                {order.gift_wrap ? "Yes" : "No"}
                {giftWrapLine ? (
                  <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                    Added as item
                  </span>
                ) : null}
              </span>
            </div>
          </Section>

          {/* Products */}
          <Section title={`Products (${(order.products || []).length})`}>
            {(order.products || []).length === 0 ? (
              <p className="text-sm text-slate-500">
                No products found for this order.
              </p>
            ) : (
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-200 overflow-hidden">
                {(order.products || []).map((p, idx) => {
                  const lineTotal =
                    Number(p?.price || 0) * Number(p?.quantity || 0);
                  const isGift = isGiftWrapItem(p);

                  return (
                    <div key={`${p?.sku || idx}`} className="flex gap-3 p-3">
                      <div className="h-12 w-12 overflow-hidden rounded-lg bg-slate-100 shrink-0">
                        {p?.img ? (
                          <img
                            src={p.img}
                            alt={p?.title || "Product"}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {p?.title || "—"}
                            </p>
                            <p className="text-xs text-slate-500">
                              {p?.variant ? `${p.variant} • ` : ""}
                              SKU: {p?.sku || "—"}
                            </p>
                          </div>

                          {isGift ? (
                            <span className="shrink-0 inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                              Gift Wrap
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-xs text-slate-500">
                            ৳{Number(p?.price || 0).toFixed(0)} ×{" "}
                            {Number(p?.quantity || 0)}
                          </p>
                          <p className="text-sm font-semibold text-slate-900">
                            ৳{Number(lineTotal).toFixed(0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Payment Summary */}
          <Section title="Payment Summary">
            <div className="rounded-xl border border-slate-200 p-4 space-y-2">
              <MoneyRow label="Subtotal" value={subtotal} />
              {giftWrapTotal > 0 ? (
                <MoneyRow label="Gift Wrap" value={giftWrapTotal} />
              ) : null}
              <MoneyRow label="Delivery Charge" value={deliveryCharge} />
              <div className="border-t border-slate-200 pt-2" />
              <MoneyRow label="Order Total" value={orderTotal} strong />
              <MoneyRow label="Amount Paid" value={amountPaid} />
              <MoneyRow
                label="Amount To Collect (Due)"
                value={amountToCollect}
                strong
              />
              {order.trxid ? (
                <Detail label="TRX ID" value={order.trxid} />
              ) : null}
            </div>
          </Section>

          {/* Dates */}
          <Section title="Dates">
            <Detail
              label="Created At"
              value={
                order.createdAt
                  ? new Date(order.createdAt).toLocaleString()
                  : "—"
              }
            />
            <Detail
              label="Updated At"
              value={
                order.updatedAt
                  ? new Date(order.updatedAt).toLocaleString()
                  : "—"
              }
            />
          </Section>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 bg-white">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {children}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <span className="text-slate-900 text-right max-w-[65%] break-words">
        {value ?? "—"}
      </span>
    </div>
  );
}

function MoneyRow({ label, value, strong }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`${strong ? "font-semibold" : ""} text-slate-900`}>
        ৳{Number(value || 0).toFixed(0)}
      </span>
    </div>
  );
}

function StatusRow({ label, type, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <StatusBadge type={type} value={value} />
    </div>
  );
}

const paymentStatusStyles = {
  Paid: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  Unpaid: "bg-rose-100 text-rose-800 ring-rose-300",
  "Partially Paid": "bg-amber-100 text-amber-800 ring-amber-300",
  Refunded: "bg-slate-100 text-slate-800 ring-slate-300",
};

const orderStatusStyles = {
  Pending: "bg-amber-50 text-amber-700 ring-amber-200",
  "At Sorting Hub": "bg-blue-50 text-blue-700 ring-blue-200",
  "In Transit": "bg-sky-50 text-sky-700 ring-sky-200",
  "At Delivery Hub": "bg-indigo-50 text-indigo-700 ring-indigo-200",
  "Out for Delivery": "bg-purple-50 text-purple-700 ring-purple-200",
  "On Hold": "bg-yellow-50 text-yellow-700 ring-yellow-200",
  Delivered: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Cancelled: "bg-rose-50 text-rose-700 ring-rose-200",
};

function StatusBadge({ type, value }) {
  const styles =
    type === "order"
      ? orderStatusStyles[value] || "bg-slate-100 text-slate-700 ring-slate-300"
      : paymentStatusStyles[value] ||
        "bg-slate-100 text-slate-700 ring-slate-300";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${styles}`}
    >
      {value || "—"}
    </span>
  );
}
